import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  ArrowLeft, 
  Bell,
  Mail,
  MessageSquare,
  Smartphone,
  Loader2,
  Check,
  Send,
  AlertCircle,
  Clock
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NotificationSettingsPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(null);
  
  const [preferences, setPreferences] = useState({
    email_enabled: false,
    email_address: "",
    sms_enabled: false,
    sms_phone: "",
    push_enabled: false,
    reminder_days: "3"
  });

  useEffect(() => {
    const storedUser = localStorage.getItem("tamba_user");
    if (!storedUser) {
      navigate("/auth");
      return;
    }
    const userData = JSON.parse(storedUser);
    setUser(userData);
    fetchPreferences(userData.id);
  }, [navigate]);

  const fetchPreferences = async (userId) => {
    try {
      const response = await axios.get(`${API}/notifications/preferences/${userId}`);
      const prefs = response.data;
      setPreferences({
        email_enabled: prefs.email_enabled || false,
        email_address: prefs.email_address || "",
        sms_enabled: prefs.sms_enabled || false,
        sms_phone: prefs.sms_phone || "",
        push_enabled: prefs.push_enabled || false,
        reminder_days: (prefs.reminder_days || 3).toString()
      });
    } catch (error) {
      console.error("Failed to fetch preferences:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/notifications/preferences`, {
        user_id: user.id,
        preferences: {
          email_enabled: preferences.email_enabled,
          email_address: preferences.email_address || null,
          sms_enabled: preferences.sms_enabled,
          sms_phone: preferences.sms_phone || null,
          push_enabled: preferences.push_enabled,
          reminder_days: parseInt(preferences.reminder_days)
        }
      });
      toast.success("Notification preferences saved!");
    } catch (error) {
      toast.error("Failed to save preferences");
    } finally {
      setSaving(false);
    }
  };

  const handleSendTest = async (type) => {
    setSendingTest(type);
    try {
      // Save first to ensure preferences are up to date
      await handleSave();
      
      const response = await axios.post(`${API}/notifications/send-test/${user.id}?notification_type=${type}`);
      if (response.data.results && response.data.results.length > 0) {
        toast.success(`Test ${type} notification sent!`);
      } else {
        toast.info(`No ${type} notification configured`);
      }
    } catch (error) {
      toast.error(`Failed to send test ${type}`);
    } finally {
      setSendingTest(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-tamba-blue" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border/50 px-4 py-3">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate("/settings")}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="font-heading text-xl font-semibold">Notifications</h1>
              <p className="text-xs text-muted-foreground">Bill reminders & alerts</p>
            </div>
          </div>
          <Button 
            onClick={handleSave}
            disabled={saving}
            className="rounded-full bg-tamba-blue hover:bg-tamba-blue/90"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
          </Button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {/* Reminder Timing */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card className="border-tamba-blue/30 bg-gradient-to-br from-tamba-blue/5 to-transparent">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Clock className="h-5 w-5 text-tamba-blue" />
                Reminder Timing
              </CardTitle>
              <CardDescription>How many days before a bill is due should we notify you?</CardDescription>
            </CardHeader>
            <CardContent>
              <Select 
                value={preferences.reminder_days} 
                onValueChange={(v) => setPreferences(prev => ({ ...prev, reminder_days: v }))}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 day before</SelectItem>
                  <SelectItem value="2">2 days before</SelectItem>
                  <SelectItem value="3">3 days before</SelectItem>
                  <SelectItem value="5">5 days before</SelectItem>
                  <SelectItem value="7">7 days before</SelectItem>
                </SelectContent>
              </Select>
            </CardContent>
          </Card>
        </motion.div>

        {/* Email Notifications */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="border-border/50">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-tamba-coral/10 flex items-center justify-center">
                    <Mail className="h-5 w-5 text-tamba-coral" />
                  </div>
                  <div>
                    <CardTitle className="font-heading text-lg">Email Notifications</CardTitle>
                    <CardDescription>Receive bill reminders via email</CardDescription>
                  </div>
                </div>
                <Switch
                  checked={preferences.email_enabled}
                  onCheckedChange={(checked) => setPreferences(prev => ({ ...prev, email_enabled: checked }))}
                />
              </div>
            </CardHeader>
            {preferences.email_enabled && (
              <CardContent className="pt-0 space-y-4">
                <div>
                  <Label>Email Address</Label>
                  <Input
                    type="email"
                    placeholder="your@email.com"
                    value={preferences.email_address}
                    onChange={(e) => setPreferences(prev => ({ ...prev, email_address: e.target.value }))}
                    className="mt-1"
                  />
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleSendTest("email")}
                  disabled={!preferences.email_address || sendingTest === "email"}
                  className="gap-2"
                >
                  {sendingTest === "email" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  Send Test Email
                </Button>
              </CardContent>
            )}
          </Card>
        </motion.div>

        {/* SMS Notifications */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card className="border-border/50">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-tamba-teal/10 flex items-center justify-center">
                    <MessageSquare className="h-5 w-5 text-tamba-teal" />
                  </div>
                  <div>
                    <CardTitle className="font-heading text-lg">SMS / Text Messages</CardTitle>
                    <CardDescription>Receive bill reminders via text</CardDescription>
                  </div>
                </div>
                <Switch
                  checked={preferences.sms_enabled}
                  onCheckedChange={(checked) => setPreferences(prev => ({ ...prev, sms_enabled: checked }))}
                />
              </div>
            </CardHeader>
            {preferences.sms_enabled && (
              <CardContent className="pt-0 space-y-4">
                <div>
                  <Label>Phone Number</Label>
                  <Input
                    type="tel"
                    placeholder="+1 234 567 8900"
                    value={preferences.sms_phone}
                    onChange={(e) => setPreferences(prev => ({ ...prev, sms_phone: e.target.value }))}
                    className="mt-1"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Include country code (e.g., +1 for US)</p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleSendTest("sms")}
                  disabled={!preferences.sms_phone || sendingTest === "sms"}
                  className="gap-2"
                >
                  {sendingTest === "sms" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  Send Test SMS
                </Button>
              </CardContent>
            )}
          </Card>
        </motion.div>

        {/* Push Notifications */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card className="border-border/50">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center">
                    <Smartphone className="h-5 w-5 text-purple-500" />
                  </div>
                  <div>
                    <CardTitle className="font-heading text-lg">Push Notifications</CardTitle>
                    <CardDescription>Receive alerts in your browser/app</CardDescription>
                  </div>
                </div>
                <Switch
                  checked={preferences.push_enabled}
                  onCheckedChange={(checked) => setPreferences(prev => ({ ...prev, push_enabled: checked }))}
                />
              </div>
            </CardHeader>
            {preferences.push_enabled && (
              <CardContent className="pt-0">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleSendTest("push")}
                  disabled={sendingTest === "push"}
                  className="gap-2"
                >
                  {sendingTest === "push" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  Send Test Push
                </Button>
              </CardContent>
            )}
          </Card>
        </motion.div>

        {/* Info Banner */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <Card className="border-tamba-yellow/30 bg-gradient-to-br from-tamba-yellow/5 to-transparent">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-tamba-yellow flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-sm">Notification System</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Notifications are currently in <strong>demo mode</strong>. 
                    In the full version, you'll receive real email, SMS, and push notifications 
                    for your upcoming bills.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* What You'll Receive */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Bell className="h-5 w-5 text-tamba-blue" />
                What You'll Receive
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <ul className="space-y-3 text-sm">
                <li className="flex items-start gap-3">
                  <Check className="h-4 w-4 text-tamba-teal mt-0.5" />
                  <div>
                    <p className="font-medium">Bill Due Reminders</p>
                    <p className="text-muted-foreground">Get notified before your bills are due</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="h-4 w-4 text-tamba-teal mt-0.5" />
                  <div>
                    <p className="font-medium">Overdue Alerts</p>
                    <p className="text-muted-foreground">Know immediately if you miss a payment</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="h-4 w-4 text-tamba-teal mt-0.5" />
                  <div>
                    <p className="font-medium">Budget Warnings</p>
                    <p className="text-muted-foreground">Alerts when you're close to your budget limit</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="h-4 w-4 text-tamba-teal mt-0.5" />
                  <div>
                    <p className="font-medium">Weekly Summary</p>
                    <p className="text-muted-foreground">A digest of your upcoming bills and spending</p>
                  </div>
                </li>
              </ul>
            </CardContent>
          </Card>
        </motion.div>
      </main>
    </div>
  );
};

export default NotificationSettingsPage;
